import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import Loading from '../components/base/loading';

const FormStateContext = createContext({
  isLoading: false,
  isSubmitting: false,
  loadError: null as unknown,
  submitError: null as unknown,
  startLoad: () => {
    void 0;
  },
  endLoad: () => {
    void 0;
  },
  startSubmit: () => {
    void 0;
  },
  endSubmit: () => {
    void 0;
  },
  setLoadError: (_error: unknown) => {
    void 0;
  },
  setSubmitError: (_error: unknown) => {
    void 0;
  }
});

interface FormStateContextProviderProps extends React.PropsWithChildren {
  endLoadDebounceMs?: number;
}

const FormStateContextProvider: React.FC<FormStateContextProviderProps> = ({
  endLoadDebounceMs = 0,
  children
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const loadingCountRef = useRef(0);
  const submitCountRef = useRef(0);
  const endLoadTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const startLoad = useCallback(() => {
    loadingCountRef.current += 1;
    clearTimeout(endLoadTimer.current);
    setIsLoading(true);
  }, [setIsLoading]);

  const endLoad = useCallback(() => {
    loadingCountRef.current -= 1;
    if (loadingCountRef.current === 0) {
      if (endLoadDebounceMs > 0) {
        clearTimeout(endLoadTimer.current);
        endLoadTimer.current = setTimeout(() => {
          if (loadingCountRef.current === 0) {
            setIsLoading(false);
          }
        }, endLoadDebounceMs);
      } else {
        setIsLoading(false);
      }
    }
  }, [endLoadDebounceMs, setIsLoading]);

  const startSubmit = useCallback(() => {
    submitCountRef.current += 1;
    setIsSubmitting(true);
  }, [setIsSubmitting]);

  const endSubmit = useCallback(() => {
    submitCountRef.current -= 1;
    if (submitCountRef.current === 0) {
      setIsSubmitting(false);
    }
  }, [setIsSubmitting]);

  const value = useMemo(
    () => ({
      isLoading,
      isSubmitting,
      startLoad,
      endLoad,
      startSubmit,
      endSubmit,
      loadError,
      setLoadError,
      submitError,
      setSubmitError
    }),
    [isLoading, isSubmitting, startLoad, endLoad, startSubmit, endSubmit, loadError, submitError]
  );

  return <FormStateContext.Provider value={value}>{children}</FormStateContext.Provider>;
};

interface FormSuspenseProps {
  loadingLabel?: string;
}

const FormSuspense: React.FC<React.PropsWithChildren<FormSuspenseProps>> = ({
  children,
  loadingLabel
}) => {
  const { isLoading, loadError } = useContext(FormStateContext);
  if (loadError !== null) {
    throw loadError instanceof Error
      ? loadError
      : new Error('Unexpected error', { cause: loadError });
  }

  const containerStyles: React.CSSProperties = {
    height: '100%',
    display: isLoading ? 'none' : undefined
  };

  return (
    <Suspense fallback={<Loading label={loadingLabel} />}>
      {isLoading && <Loading label={loadingLabel} />}
      <div style={containerStyles}>{children}</div>
    </Suspense>
  );
};

export default FormStateContext;
export { FormStateContextProvider, FormSuspense };
